<script setup lang="ts">
import { mdiYoutube } from '@mdi/js'
import type { DialogStep } from '~/types/interfaces'
// 自動 import の名前（置き場所で接頭辞が変わる）に頼らず、直接読み込む（#966: 置き場所を移したら dev で空になった）
import SkillLearnedBody from '../SkillLearnedBody.vue'

/**
 * イベントダイアログの本文（#898）。
 *
 * **器（Dialog / LargeDialog）に依存しない**ように、`DialogStep` の描画だけをここに置く。
 * 器の選択とボタン配線は `EventDialog.vue` が持つ。分けてあるのは、以前 `LargeEventDialog.vue`
 * が `EventDialog.vue` のコピーとして枝分かれし、video 分岐・評価ボタン（#654）・
 * speaker アイコンの float レイアウトが片方にだけ入る形で腐っていたため。
 */
const props = defineProps<{ params: DialogStep }>()

const dialogStore = useDialogStore()

const { resolveIcon, resolveImage } = useStoryImage()

// ダイアログに 👍/🤔/👎 評価ボタンを描画する条件 (#654)。params.ratingTarget が set の場合のみ。
// ボタン本体は UserRating（羽根筆のパネルの相談の答えでも使う。#954）
const ratingTarget = computed(() => {
  const params = props.params
  if (!params || params.type !== 'dialog') return null
  return params.ratingTarget ?? null
})

const onPlayVideo = async () => {
  const videoId = props.params.image
  if (videoId) {
    await dialogStore.showDialogAsync('video', { params: {
      type: 'dialog',
      speaker: 'video',
      title: props.params.title ?? '',
      text: videoId,
      actions: {
        cancel: {
          label: 'とじる',
          disabled: false,
          sound: 'cancel',
        },
      },
    } })
  }
}
</script>

<template>
  <!-- video -->
  <template v-if="params.speaker === 'video'">
    <MDC :value="params.text" />
    <v-container class="w-100 fluid">
      <v-row justify="center">
        <v-col cols="auto">
          <v-btn
            color="red"
            variant="elevated"
            class="youtube-btn"
            rounded="lg"
            @click="onPlayVideo"
          >
            <v-icon
              start
              size="x-large"
            >
              {{ mdiYoutube }}
            </v-icon>
            動画
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </template>
  <!-- 冒険の手記（#899 / #900）: large の器のときは、額に入った挿絵を左に float させて
       本文が回り込む「物語のページ」にする。絵を見せたまま読ませたいため。
       通常サイズの器では従来どおり本文の上に横長の hero image を敷く。 -->
  <div
    v-else-if="params.speaker === 'image' && params.size === 'large'"
    class="memoir-block"
  >
    <!-- 幅はこの div が持つ（MemoirFrame 自身は width:100% で親に従う）。子の root に
         親の class を直接当てると、同じ specificity の width が注入順で勝ち負けする。 -->
    <div class="memoir-block__frame">
      <MemoirFrame
        :src="resolveImage(params.image)"
        :alt="params.title"
      />
    </div>
    <MDC :value="params.text" />
  </div>
  <!-- スキル獲得のお知らせ（#966）: 本文は新しく覚えたスキルの ID。スキル一覧の左の列と同じカードで並べる -->
  <SkillLearnedBody
    v-else-if="params.speaker === 'skills'"
    :ids="params.text"
  />
  <!-- with hero image -->
  <template v-else-if="params.speaker === 'image'">
    <v-img
      :src="resolveImage(params.image!)"
      width="100%"
      class="mb-3 hero-image"
      cover
    />
    <MDC :value="params.text" />
  </template>
  <!-- with speaker (float layout: アイコンを左上に float させて、本文が周りを
       回り込む DTP 風レイアウト。v-row/v-col の grid 分割だと、code block の
       ような幅広コンテンツが折り返さず 100% 要求して右ペインを下に押しやる
       問題が起きるので、float で本文を 100% 幅に保ったまま回り込ませる。) -->
  <div
    v-else-if="params.speaker && params.speaker !== 'system'"
    class="speaker-block"
  >
    <v-img
      :src="resolveIcon(params.speaker)"
      cover
      class="speaker-icon"
    />
    <MDC :value="params.text" />
  </div>
  <!-- text only -->
  <div v-else>
    <MDC :value="params.text" />
  </div>

  <!-- ユーザー評価 (#654) -->
  <div
    v-if="ratingTarget"
    class="rating-section"
    data-testid="user-rating"
  >
    <UserRating :target="ratingTarget" />
  </div>
</template>

<style scoped>
/* Speaker icon は本文の左上に float。これでテキストが回り込む（DTP の段組みと
   同じ仕組み）。コードブロックなど幅広の block 要素も、float の高さを超えれば
   自然に 100% 幅で下に流れる。 */
.speaker-icon {
  /* デスクトップは従来サイズ（64 / 12u）を維持。 */
  width: max(64px, calc(12 * var(--u)));
  height: max(64px, calc(12 * var(--u)));
  float: left;
  margin-right: max(8px, calc(1 * var(--u)));
  margin-bottom: max(4px, calc(0.5 * var(--u)));
  flex: none;
}

/* #721: モバイルの浮遊ダイアログでは話者アイコン（せんぱい等の顔）を +50% に
   拡大（64→96 / 12→18u）。顔が小さく寂しく見えたため。デスクトップは従来サイズ
   のままにするため、拡大はモバイル幅（Dialog.vue と同じ 768px ブレークポイント）
   に限定する。 */
@media (max-width: 768px) {
  .speaker-icon {
    width: max(96px, calc(18 * var(--u)));
    height: max(96px, calc(18 * var(--u)));
  }
}

/* float clearfix: 本文が icon より短かったときに後続要素（rating 等）が
   icon と重ならないようにする。 */
.speaker-block::after {
  content: "";
  display: block;
  clear: both;
}

/* hero image（speaker: image）の高さ。従来の 232px 固定を維持する。 */
.hero-image {
  height: 232px;
}

/* 冒険の手記の額（#899 / #900）。話者アイコンと同じ float で本文を回り込ませる。
   幅を **本文領域に対する割合**で持つのが要点 —— 額は 900:1248 なので 35% だと
   高さが本文領域（47.5u）にちょうど収まり、1920x1080 では 額 479x664 / 本文段 約 870px
   ≒ 32 字 x 14 行 ≒ 440 字/ページになる（doc/design/art_direction.md §6.3 の実寸）。
   割合で持たせてあるので、器が小さいときも本文段が潰れない。 */
.memoir-block__frame {
  float: left;
  width: 35%;
  margin: 0 max(12px, calc(1.5 * var(--u))) max(8px, calc(1 * var(--u))) 0;
}

/* float clearfix（speaker-block と同じ理由）。 */
.memoir-block::after {
  content: "";
  display: block;
  clear: both;
}

/* モバイル（#721 と同じ 768px ブレークポイント）: シートは最大 560px しかないので、
   同じ float だと本文段が 10 字ほどに潰れる。絵を上に置いて本文を下に流す。 */
@media (max-width: 768px) {
  .memoir-block__frame {
    float: none;
    width: 52%;
    max-width: 240px;
    margin: 0 auto max(8px, calc(1 * var(--u)));
  }
}

.youtube-btn {
  width: max(100px, calc(14.06 * var(--u)));
  height: max(33.33px, calc(4.69 * var(--u)));
  font-size: max(10.6px, calc(1.48 * var(--u)));
}

/* 本文 (.fantasy-dialog-text) の右下に重ねて表示する (#654)。
   本文が長くて overflow しても、本文と一緒に流れず常に同じ位置で見えるようにする。
   親 .fantasy-dialog-text は器（Dialog.vue / LargeDialog.vue）側で
   position: relative にしている。 */
.rating-section {
  --rating-bg: rgba(245, 211, 140, 0.85);
  position: absolute;
  right: max(4px, calc(0.5 * var(--u)));
  bottom: max(2px, calc(0.25 * var(--u)));
  display: flex;
  align-items: center;
  z-index: 1;
}
</style>
